import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import {
  getCookieConsent,
  setCookieConsent,
  COOKIE_CONSENT_ACCEPTED,
  COOKIE_CONSENT_ESSENTIAL,
} from "../lib/cookieConsent";

/**
 * GDPR / PECR cookie banner.
 *
 * Shows only if the user hasn't made a choice yet. "Accept all" enables
 * analytics (PostHog); "Reject non-essential" lets them through without
 * tracking. Their choice persists in localStorage and dispatches a window
 * event so `PostHogLoader` can react.
 *
 * Hidden on admin / account / screener routes (sensitive flows).
 */
export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show inside iframes (Emergent preview) or on routes where it would
    // get in the way (admin, magic-link landings).
    if (typeof window === "undefined") return;
    const path = window.location.pathname || "";
    const isSensitive =
      path.startsWith("/admin") ||
      path.startsWith("/account") ||
      path.startsWith("/screener");
    const choice = getCookieConsent();
    if (!choice && !isSensitive) {
      // Tiny delay so it doesn't jump into view during hero animations.
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const accept = () => {
    setCookieConsent(COOKIE_CONSENT_ACCEPTED);
    setVisible(false);
  };
  const reject = () => {
    setCookieConsent(COOKIE_CONSENT_ESSENTIAL);
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md z-[60] animate-[bannerIn_280ms_ease-out_both]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="relative rounded-2xl bg-[var(--dark-bg)] text-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)] border border-white/10 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-16 -right-12 w-44 h-44 rounded-full bg-[var(--lime)]/12 blur-3xl pointer-events-none"
        />
        <button
          type="button"
          onClick={reject}
          aria-label="Close , reject non-essential"
          className="absolute top-3 right-3 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/5 transition"
          data-testid="cookie-banner-close"
        >
          <X size={16} />
        </button>

        <div className="relative z-10 p-5 md:p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--lime)]/15">
              <Cookie size={14} className="text-[var(--lime)]" />
            </span>
            <span className="font-['Outfit'] font-bold text-[15px] tracking-tight">
              We use cookies
            </span>
          </div>
          <p className="text-[13.5px] text-white/75 leading-relaxed mb-4">
            We use essential cookies to make MediTalk work and optional
            analytics cookies to understand how the site is used so we can keep
            improving it. Analytics stay off unless you accept.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={accept}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-[13px] hover:bg-[var(--lime-dark)] transition-colors"
              data-testid="cookie-banner-accept"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={reject}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-white/[0.06] text-white font-semibold text-[13px] border border-white/15 hover:bg-white/[0.1] transition-colors"
              data-testid="cookie-banner-reject"
            >
              Essential only
            </button>
          </div>
          <p className="text-[11px] text-white/55 mt-3">
            Read our{" "}
            <Link
              to="/cookies"
              className="text-white underline decoration-[var(--lime)] decoration-2 underline-offset-2 hover:decoration-[var(--lime-dark)]"
              data-testid="cookie-banner-policy-link"
            >
              cookie policy
            </Link>
            . You can change your mind any time.
          </p>
        </div>
      </div>
      <style>{`
        @keyframes bannerIn {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default CookieBanner;
